import React from 'react';
import { FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';

export type RebateCheckoutTreatment = 'price_reduction' | 'discount_line';

export interface RebateTreatmentSetting {
  treatment: RebateCheckoutTreatment | null;
  chosen: boolean;
  options: Array<{ value: RebateCheckoutTreatment; label: string; description: string }>;
  note: string;
}

export const REBATE_TREATMENT_QUERY_KEY = ['store-rebate-treatment'] as const;

/** `GET /store/settings/rebate-treatment`: the store's choice plus the option copy the server owns. */
export function useRebateTreatmentSetting(enabled: boolean) {
  const { t } = useAppTranslation();
  return useQuery({
    queryKey: REBATE_TREATMENT_QUERY_KEY,
    queryFn: async (): Promise<RebateTreatmentSetting> => {
      const response = await makeApiCall('/store/settings/rebate-treatment', {
        headers: await getAuthHeaders(),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body?.success) {
        throw new Error(body?.error?.message || body?.message || t('store.rebateTreatment.loadError'));
      }
      return body.data as RebateTreatmentSetting;
    },
    enabled,
  });
}

/** `PUT /store/settings/rebate-treatment` (owner/admin). Also re-syncs the store's discounts server side when the value changed. */
export async function saveRebateTreatment(
  treatment: RebateCheckoutTreatment,
  fallbackError: string,
): Promise<RebateTreatmentSetting> {
  const response = await makeApiCall('/store/settings/rebate-treatment', {
    method: 'PUT',
    headers: { ...(await getAuthHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ treatment }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body?.success) {
    throw new Error(body?.error?.message || body?.message || fallbackError);
  }
  return body.data as RebateTreatmentSetting;
}

export interface RebateTreatmentChoiceProps {
  setting: RebateTreatmentSetting;
  value: RebateCheckoutTreatment | '';
  onChange: (value: RebateCheckoutTreatment) => void;
  /**
   * True when this surface is a Shopify connection. Store Settings passes whether
   * any active Shopify integration exists; the walkthrough passes the current platform.
   * The Plus sentence is hidden when this is false.
   */
  hasShopify?: boolean;
  /** True when a connected Shopify shop is on Plus (recorded on the integration, never asked). */
  shopifyPlus?: boolean;
}

/**
 * The two ways an instant rebate reaches checkout, with the server's own copy
 * and tax note. Shared by the Store Settings card and the setup walkthrough so
 * the wording lives in one place.
 */
export const RebateTreatmentChoice: React.FC<RebateTreatmentChoiceProps> = ({
  setting,
  value,
  onChange,
  hasShopify = false,
  shopifyPlus = false,
}) => {
  const { t } = useAppTranslation();
  return (
  <>
    <FormControl>
      <RadioGroup value={value} onChange={(event) => onChange(event.target.value as RebateCheckoutTreatment)}>
        {setting.options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            sx={{ alignItems: 'flex-start', mb: 1.5, '& .MuiRadio-root': { pt: 0.5 } }}
            label={
              <Stack spacing={0.25} sx={{ py: 0.25 }}>
                <Typography variant="body2" fontWeight={600}>
                  {option.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.description}
                </Typography>
              </Stack>
            }
          />
        ))}
        {/* Rebate after tax: always shown, not selectable yet (the mechanism ships later). */}
        <FormControlLabel
          value="after_tax"
          disabled
          control={<Radio />}
          sx={{ alignItems: 'flex-start', mb: 1.5, '& .MuiRadio-root': { pt: 0.5 } }}
          label={
            <Stack spacing={0.25} sx={{ py: 0.25 }}>
              <Typography variant="body2" fontWeight={600}>
                {t('store.rebateTreatment.afterTax.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('store.rebateTreatment.afterTax.description')}
              </Typography>
              <Typography variant="body2" color="text.secondary" data-testid="after-tax-status">
                {hasShopify && !shopifyPlus
                  ? t('store.rebateTreatment.afterTax.requiresPlus')
                  : t('store.rebateTreatment.afterTax.comingSoon')}
              </Typography>
            </Stack>
          }
        />
      </RadioGroup>
    </FormControl>
    <Typography variant="body2" color="text.secondary">
      {setting.note}
    </Typography>
  </>
  );
};

export default RebateTreatmentChoice;
